import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { css } from '@emotion/react';
import { AppLanguage } from '@anytag/shared-types';
import { THEME } from '@anytag/shared-constants';
import { InfluencerAvatar } from '@anytag/shared-molecules';
import { YoutubeAnalyticsTrendPayload } from '@anytag/graphql';
import { formatIntNumber, localizedDateFormatter, formatDate } from '@anytag/shared-utils';
import { useGenerateInfluencerProfileLink, usePostDetailsParam } from '@anytag/shared-hooks';
import { Anchor, MediaPreview, TextCutter, TableCell } from '@anytag/shared-atoms';
import { useAnalyticsTrendingYoutubePostsFilter } from '../../AnalyticsTrendingYoutubePostsFilter';

export const useAnalyticsTrendingYoutubePostsTable = () => {
  const { t, i18n } = useTranslation();
  const { setPostDetailsParam } = usePostDetailsParam();
  const { page, limit } = useAnalyticsTrendingYoutubePostsFilter();
  const { generateProfileLink } = useGenerateInfluencerProfileLink();

  const columns: ColumnDef<YoutubeAnalyticsTrendPayload>[] = [
    {
      size: 500,
      accessorKey: 'post',
      header: t<string>('HeaderColumn.Post'),
      cell: ({ row }) => {
        const rank = page && limit ? (page - 1) * limit + row.index + 1 : 0;

        return (
          <div
            css={{ maxWidth: '500px', display: 'flex', alignItems: 'center', padding: '8px 0', position: 'relative' }}
          >
            <div css={[styles.rank, { backgroundColor: rank < 4 ? '#FF2B52' : '#27313B' }]}>{rank}</div>
            <MediaPreview
              src={row.original.videoThumbnail}
              css={{ width: '184px', height: '104px' }}
              onClick={() => setPostDetailsParam(row.original.id)}
            />
            <div css={{ marginLeft: '8px' }}>
              <Anchor
                onClick={(e) => {
                  e.preventDefault();
                  setPostDetailsParam(row.original.id);
                }}
                label={row.original.videoTitle}
              />
              <TextCutter
                text={row.original.videoCaption}
                lines={4}
                css={{ fontSize: '12px', color: THEME.text.colors.gray.lv3 }}
              />
            </div>
          </div>
        );
      }
    },
    {
      accessorKey: 'channel',
      header: t<string>('HeaderColumn.Channel'),
      cell: ({ row }) => (
        <TableCell.Link
          css={{ maxWidth: '200px', minWidth: '100px', display: 'flex', alignItems: 'center' }}
          {...generateProfileLink(
            row.original.influencerId?.toString(),
            row.original.socialAccountId
              ? { filter: { id: String(row.original.socialAccountId), sm: 'YOUTUBE' } }
              : undefined
          )}
        >
          <InfluencerAvatar size="32px" src={row.original.channelAvatar} css={{ borderRadius: '50%' }} asChild />
          <TableCell.Text value={row.original.channel} css={{ marginLeft: '8px' }} />
        </TableCell.Link>
      )
    },
    {
      accessorKey: 'views',
      header: t<string>('HeaderColumn.View'),
      meta: { align: 'right' },
      cell: ({ row }) => <TableCell.Number css={{ minWidth: '100px' }} value={formatIntNumber(row.original.views)} />
    },
    {
      accessorKey: 'comments',
      header: t<string>('HeaderColumn.Comments'),
      meta: { align: 'right' },
      cell: ({ row }) => <TableCell.Number css={{ minWidth: '100px' }} value={formatIntNumber(row.original.comments)} />
    },
    {
      accessorKey: 'postDate',
      header: t<string>('HeaderColumn.Post date'),
      cell: ({ row }) => {
        const parsedDate = row.original.postDate?.replace('Z', '') || null;

        return (
          <TableCell.Text
            css={{ minWidth: '150px' }}
            value={`${localizedDateFormatter(parsedDate, 'MMM dd', i18n.language as AppLanguage)} at ${formatDate(
              parsedDate,
              'hh:mm a'
            )}`}
          />
        );
      }
    }
  ];

  return { columns };
};
const styles = {
  rank: css({
    top: 0,
    left: 0,
    zIndex: 1,
    width: '24px',
    height: '24px',
    display: 'flex',
    fontSize: '16px',
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    color: THEME.text.colors.white
  })
};
